/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component} from '@angular/core';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';

/**
 * Prompts the user for a required reason when one or more rows have their Development Status
 * changed away from Approved (back to Draft or Pending). Modeled on the config.data callback
 * pattern used by {@link AdvancedFilterModalComponent} - config.data.onConfirm/onCancel, not
 * ref.close(payload). config.data.affectedRowLabels lists the rows this reason will apply to
 * (one shared reason for the whole batch).
 */
@Component({
    selector: 'app-development-status-reason-dialog',
    template: `
        <div class="p-fluid">
            <p>
                Changing Development Status from <strong>Approved</strong> back to Draft or Pending requires a
                reason. This reason will be recorded in the change history.
            </p>
            @if (affectedRowLabels.length > 0) {
                <p><strong>Affected rows:</strong></p>
                <ul class="affected-rows">
                    @for (label of affectedRowLabels; track label) {
                        <li>{{ label }}</li>
                    }
                </ul>
            }
            <textarea [(ngModel)]="reason" rows="4" class="p-inputtextarea p-component"
                      placeholder="Enter a reason for this change..."></textarea>
            <br/>
            <br/>
            <div class="button-row">
                <button pButton type="button" label="Confirm" (click)="confirm()"
                        [disabled]="!reason?.trim()" class="p-mr-2"></button>
                <button pButton type="button" label="Cancel" (click)="cancel()" class="p-button-secondary"></button>
            </div>
        </div>
    `,
    styles: [`
        .affected-rows {
            max-height: 120px;
            overflow-y: auto;
        }
        textarea {
            width: 100%;
        }
        .button-row {
            display: flex;
            justify-content: flex-end;
            margin-top: 1rem;
        }
        button {
            width: 100px;
        }
    `],
    standalone: false
})
export class DevelopmentStatusReasonDialogComponent {
    affectedRowLabels: string[] = [];
    reason = '';

    constructor(public ref: DynamicDialogRef, public config: DynamicDialogConfig) {
        this.affectedRowLabels = this.config.data?.affectedRowLabels ?? [];
    }

    confirm(): void {
        this.config.data.onConfirm?.(this.reason.trim());
        this.ref.close();
    }

    cancel(): void {
        this.config.data.onCancel?.();
        this.ref.close();
    }
}
